import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { useSearch } from '@/common/apis/services/search/search';
import Alert from '@/common/components/atom/alert';
import Button from '@/common/components/atom/button';
import Loading from '@/common/components/atom/loading';
import Modal from '@/common/components/atom/modal';
import Text from '@/common/components/atom/text';
import ChevronIcon from '@/common/components/icons/chevron';
import useModal from '@/common/hooks/useModal';
import { splunkInstance } from '@/common/services/splunk';
import { doctorSlugFromSearchHit, useRaviCardRate } from '@/modules/profile/hooks/useDoctorRateDetails';
import random from 'lodash/random';
import { useMemo, useRef } from 'react';
import { useSearch as useSearchUtils } from '../../hooks/useSearch';
import { useSearchRouting } from '../../hooks/useSearchRouting';
import SearchCard from '../card/card';
// import استاتیک (مثل marker.png در atom/map) تا زیر /_next/static/media سرو شود؛
// مسیر /images/* روی www به اپ Next نمی‌رسد (به یک origin قدیمی می‌رود و 404 می‌دهد) — DR-08.
import doctorGroupImage from './doctor-group.webp';

/**
 * `hold`: the results column is still showing its loading skeleton. The banner then keeps what it
 * showed last — nothing, right after a client navigation — and takes the new search's shape together
 * with the list; otherwise it grew from the no-category shape by ~75px under the skeleton
 * (docs/performance.md §۳.۲۱).
 */
export const ConsultBanner = ({ hold = false }: { hold?: boolean }) => {
  const live = useSearchUtils();
  const shown = useRef(hold ? null : live);
  if (!hold) shown.current = live;
  const { selectedCategory, selectedSubCategory, searchCity } = shown.current ?? live;
  // Follows the URL at once: tapping the banner itself switches to consult and hides it, as before.
  // «نوبت حضوری» chosen: the patient asked for in-person, so no online-visit invitation (functions/inPersonView.ts).
  const { isConsult, isInPersonView } = live;
  const { changeRoute } = useSearchRouting();
  const { handleOpen, handleClose, modalProps } = useModal();

  const categorySlug = selectedSubCategory?.value ?? selectedCategory?.value;

  const searchData = useSearch(
    {
      route: decodeURIComponent(`${searchCity?.en_slug ?? 'ir'}/${categorySlug}`),
      query: {
        turn_type: 'consult',
      },
    },
    {
      enabled: modalProps.isOpen,
    },
  );
  const substituteDoctor = useMemo(() => searchData.data?.search?.result?.[random(0, 2)] ?? {}, [searchData.data]);
  const raviRate = useRaviCardRate(doctorSlugFromSearchHit(substituteDoctor));

  const handleClick = () => {
    changeRoute({
      query: {
        turn_type: 'consult',
      },
    });
  };

  const handleOpenSubstituteDoctorModal = () => {
    splunkInstance('search').sendEvent({
      group: 'search_visit_online_doctor_recommendation',
      type: 'search_visit_online_doctor_recommendation-open-modal',
    });
    handleOpen();
  };

  const handleClickDcotorCardDoctor = ({ url, doctor }: { url: string; doctor?: any }) => {
    splunkInstance('search').sendEvent({
      group: 'search_visit_online_doctor_recommendation',
      type: 'search_visit_online_doctor_recommendation-click-doctor-card',
      event: {
        slug: url.replace('/dr/', ''),
      },
    });
    location.assign(onlineVisitFactorUrl(doctor ?? { url }));
  };

  const handleClickMoreDoctors = () => {
    splunkInstance('search').sendEvent({
      group: 'search_visit_online_doctor_recommendation',
      type: 'search_visit_online_doctor_recommendation-click-more-doctors',
    });
    handleClose();
    handleClick();
  };

  if (isConsult || isInPersonView || !shown.current) return null;

  if (!selectedCategory?.title) {
    return (
      <div
        onClick={handleClick}
        className="relative flex items-center p-5 px-6 mb-3 overflow-hidden text-teal-700 bg-white rounded-lg md:hidden space-s-3"
      >
        <div className="flex items-center justify-center before:rounded-full before:content before:absolute before:w-10 before:h-10 before:bg-teal-700 before:opacity-30 before:animate-ping">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 473.806 473.806"
            xmlSpace="preserve"
            width="25"
            height="25px"
            fill="currentColor"
            className=""
          >
            <g>
              <g>
                <path
                  d="M374.456,293.506c-9.7-10.1-21.4-15.5-33.8-15.5c-12.3,0-24.1,5.3-34.2,15.4l-31.6,31.5c-2.6-1.4-5.2-2.7-7.7-4
			c-3.6-1.8-7-3.5-9.9-5.3c-29.6-18.8-56.5-43.3-82.3-75c-12.5-15.8-20.9-29.1-27-42.6c8.2-7.5,15.8-15.3,23.2-22.8
			c2.8-2.8,5.6-5.7,8.4-8.5c21-21,21-48.2,0-69.2l-27.3-27.3c-3.1-3.1-6.3-6.3-9.3-9.5c-6-6.2-12.3-12.6-18.8-18.6
			c-9.7-9.6-21.3-14.7-33.5-14.7s-24,5.1-34,14.7c-0.1,0.1-0.1,0.1-0.2,0.2l-34,34.3c-12.8,12.8-20.1,28.4-21.7,46.5
			c-2.4,29.2,6.2,56.4,12.8,74.2c16.2,43.7,40.4,84.2,76.5,127.6c43.8,52.3,96.5,93.6,156.7,122.7c23,10.9,53.7,23.8,88,26
			c2.1,0.1,4.3,0.2,6.3,0.2c23.1,0,42.5-8.3,57.7-24.8c0.1-0.2,0.3-0.3,0.4-0.5c5.2-6.3,11.2-12,17.5-18.1c4.3-4.1,8.7-8.4,13-12.9
			c9.9-10.3,15.1-22.3,15.1-34.6c0-12.4-5.3-24.3-15.4-34.3L374.456,293.506z M410.256,398.806
			C410.156,398.806,410.156,398.906,410.256,398.806c-3.9,4.2-7.9,8-12.2,12.2c-6.5,6.2-13.1,12.7-19.3,20
			c-10.1,10.8-22,15.9-37.6,15.9c-1.5,0-3.1,0-4.6-0.1c-29.7-1.9-57.3-13.5-78-23.4c-56.6-27.4-106.3-66.3-147.6-115.6
			c-34.1-41.1-56.9-79.1-72-119.9c-9.3-24.9-12.7-44.3-11.2-62.6c1-11.7,5.5-21.4,13.8-29.7l34.1-34.1c4.9-4.6,10.1-7.1,15.2-7.1
			c6.3,0,11.4,3.8,14.6,7c0.1,0.1,0.2,0.2,0.3,0.3c6.1,5.7,11.9,11.6,18,17.9c3.1,3.2,6.3,6.4,9.5,9.7l27.3,27.3
			c10.6,10.6,10.6,20.4,0,31c-2.9,2.9-5.7,5.8-8.6,8.6c-8.4,8.6-16.4,16.6-25.1,24.4c-0.2,0.2-0.4,0.3-0.5,0.5
			c-8.6,8.6-7,17-5.2,22.7c0.1,0.3,0.2,0.6,0.3,0.9c7.1,17.2,17.1,33.4,32.3,52.7l0.1,0.1c27.6,34,56.7,60.5,88.8,80.8
			c4.1,2.6,8.3,4.7,12.3,6.7c3.6,1.8,7,3.5,9.9,5.3c0.4,0.2,0.8,0.5,1.2,0.7c3.4,1.7,6.6,2.5,9.9,2.5c8.3,0,13.5-5.2,15.2-6.9
			l34.2-34.2c3.4-3.4,8.8-7.5,15.1-7.5c6.2,0,11.3,3.9,14.4,7.3c0.1,0.1,0.1,0.1,0.2,0.2l55.1,55.1
			C420.456,377.706,420.456,388.206,410.256,398.806z"
                ></path>{' '}
                <path
                  d="M256.056,112.706c26.2,4.4,50,16.8,69,35.8s31.3,42.8,35.8,69c1.1,6.6,6.8,11.2,13.3,11.2c0.8,0,1.5-0.1,2.3-0.2
			c7.4-1.2,12.3-8.2,11.1-15.6c-5.4-31.7-20.4-60.6-43.3-83.5s-51.8-37.9-83.5-43.3c-7.4-1.2-14.3,3.7-15.6,11
			S248.656,111.506,256.056,112.706z"
                ></path>{' '}
                <path
                  d="M473.256,209.006c-8.9-52.2-33.5-99.7-71.3-137.5s-85.3-62.4-137.5-71.3c-7.3-1.3-14.2,3.7-15.5,11
			c-1.2,7.4,3.7,14.3,11.1,15.6c46.6,7.9,89.1,30,122.9,63.7c33.8,33.8,55.8,76.3,63.7,122.9c1.1,6.6,6.8,11.2,13.3,11.2
			c0.8,0,1.5-0.1,2.3-0.2C469.556,223.306,474.556,216.306,473.256,209.006z"
                ></path>
              </g>
            </g>
          </svg>
        </div>
        <Text fontWeight="medium" fontSize="sm" className="text-balance">
          همین الان با پزشک متخصص {selectedCategory?.title} گفت‌وگو کنید
        </Text>
      </div>
    );
  }

  return (
    <div
      onClick={handleOpenSubstituteDoctorModal}
      className="relative flex flex-col items-center p-4 space-y-4 mb-3 overflow-hidden text-teal-700 bg-white rounded-lg md:hidden"
    >
      <div className="flex items-center space-s-2 px-3">
        <div className="flex items-center justify-center before:rounded-full before:content before:absolute before:w-10 before:h-10 before:bg-teal-700 before:opacity-30 before:animate-ping">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 473.806 473.806"
            xmlSpace="preserve"
            width="25"
            height="25px"
            fill="currentColor"
            className=""
          >
            <g>
              <g>
                <path
                  d="M374.456,293.506c-9.7-10.1-21.4-15.5-33.8-15.5c-12.3,0-24.1,5.3-34.2,15.4l-31.6,31.5c-2.6-1.4-5.2-2.7-7.7-4
			c-3.6-1.8-7-3.5-9.9-5.3c-29.6-18.8-56.5-43.3-82.3-75c-12.5-15.8-20.9-29.1-27-42.6c8.2-7.5,15.8-15.3,23.2-22.8
			c2.8-2.8,5.6-5.7,8.4-8.5c21-21,21-48.2,0-69.2l-27.3-27.3c-3.1-3.1-6.3-6.3-9.3-9.5c-6-6.2-12.3-12.6-18.8-18.6
			c-9.7-9.6-21.3-14.7-33.5-14.7s-24,5.1-34,14.7c-0.1,0.1-0.1,0.1-0.2,0.2l-34,34.3c-12.8,12.8-20.1,28.4-21.7,46.5
			c-2.4,29.2,6.2,56.4,12.8,74.2c16.2,43.7,40.4,84.2,76.5,127.6c43.8,52.3,96.5,93.6,156.7,122.7c23,10.9,53.7,23.8,88,26
			c2.1,0.1,4.3,0.2,6.3,0.2c23.1,0,42.5-8.3,57.7-24.8c0.1-0.2,0.3-0.3,0.4-0.5c5.2-6.3,11.2-12,17.5-18.1c4.3-4.1,8.7-8.4,13-12.9
			c9.9-10.3,15.1-22.3,15.1-34.6c0-12.4-5.3-24.3-15.4-34.3L374.456,293.506z M410.256,398.806
			C410.156,398.806,410.156,398.906,410.256,398.806c-3.9,4.2-7.9,8-12.2,12.2c-6.5,6.2-13.1,12.7-19.3,20
			c-10.1,10.8-22,15.9-37.6,15.9c-1.5,0-3.1,0-4.6-0.1c-29.7-1.9-57.3-13.5-78-23.4c-56.6-27.4-106.3-66.3-147.6-115.6
			c-34.1-41.1-56.9-79.1-72-119.9c-9.3-24.9-12.7-44.3-11.2-62.6c1-11.7,5.5-21.4,13.8-29.7l34.1-34.1c4.9-4.6,10.1-7.1,15.2-7.1
			c6.3,0,11.4,3.8,14.6,7c0.1,0.1,0.2,0.2,0.3,0.3c6.1,5.7,11.9,11.6,18,17.9c3.1,3.2,6.3,6.4,9.5,9.7l27.3,27.3
			c10.6,10.6,10.6,20.4,0,31c-2.9,2.9-5.7,5.8-8.6,8.6c-8.4,8.6-16.4,16.6-25.1,24.4c-0.2,0.2-0.4,0.3-0.5,0.5
			c-8.6,8.6-7,17-5.2,22.7c0.1,0.3,0.2,0.6,0.3,0.9c7.1,17.2,17.1,33.4,32.3,52.7l0.1,0.1c27.6,34,56.7,60.5,88.8,80.8
			c4.1,2.6,8.3,4.7,12.3,6.7c3.6,1.8,7,3.5,9.9,5.3c0.4,0.2,0.8,0.5,1.2,0.7c3.4,1.7,6.6,2.5,9.9,2.5c8.3,0,13.5-5.2,15.2-6.9
			l34.2-34.2c3.4-3.4,8.8-7.5,15.1-7.5c6.2,0,11.3,3.9,14.4,7.3c0.1,0.1,0.1,0.1,0.2,0.2l55.1,55.1
			C420.456,377.706,420.456,388.206,410.256,398.806z"
                ></path>{' '}
                <path
                  d="M256.056,112.706c26.2,4.4,50,16.8,69,35.8s31.3,42.8,35.8,69c1.1,6.6,6.8,11.2,13.3,11.2c0.8,0,1.5-0.1,2.3-0.2
			c7.4-1.2,12.3-8.2,11.1-15.6c-5.4-31.7-20.4-60.6-43.3-83.5s-51.8-37.9-83.5-43.3c-7.4-1.2-14.3,3.7-15.6,11
			S248.656,111.506,256.056,112.706z"
                ></path>{' '}
                <path
                  d="M473.256,209.006c-8.9-52.2-33.5-99.7-71.3-137.5s-85.3-62.4-137.5-71.3c-7.3-1.3-14.2,3.7-15.5,11
			c-1.2,7.4,3.7,14.3,11.1,15.6c46.6,7.9,89.1,30,122.9,63.7c33.8,33.8,55.8,76.3,63.7,122.9c1.1,6.6,6.8,11.2,13.3,11.2
			c0.8,0,1.5-0.1,2.3-0.2C469.556,223.306,474.556,216.306,473.256,209.006z"
                ></path>
              </g>
            </g>
          </svg>
        </div>
        <Text fontWeight="medium" fontSize="base" className="text-balance">
          همین الان با پزشک متخصص {selectedCategory?.title} گفت‌وگو کنید
        </Text>
      </div>
      <div className="flex items-end justify-between w-full">
        {/*
          DR-08 §۱۹: this used to point at /cp/img/doctor-group.svg — a **1.6MB** SVG (1197KB over
          the wire) whose entire payload is four base64-embedded full-resolution photographs, drawn
          into a 144×44 box. Measured on production it started downloading at ~600ms, i.e. it sat in
          the critical path competing with the render-blocking CSS and the webfont; on a 1.6Mbps
          connection 1.2MB alone is several seconds of the user's bandwidth.
          Replaced by a 3x raster of the exact same artwork (432×132 WebP, 21KB — 98% smaller).
          ⚠️ NOT in public/: requests to /images/* on www never reach this Next app (an Arvan/legacy
          static origin answers them with a 404), so the file is bundled via static import and served
          from /_next/static/media/… which provably routes to the app and is edge-cached.
          `decoding="async"` keeps the decode off the main thread, which already has ~9s of work.
        */}
        <img
          src={doctorGroupImage.src}
          alt="ویزیت آنلاین"
          height={44}
          width={144}
          decoding="async"
        />
        <Button size="sm" className="w-32 justify-between items-center">
          <Text>گفت‌وگو با پزشک</Text>
          <ChevronIcon dir="left" />
        </Button>
      </div>

      <Modal name="consult-banner" bodyClassName="p-3" title="" {...modalProps}>
        {(searchData.isLoading || !substituteDoctor?.url) && (
          <div className="flex justify-center w-full">
            <Loading className="w-8 h-8 my-8 " />
          </div>
        )}
        {searchData.isSuccess && substituteDoctor?.url && (
          <div className="flex flex-col space-y-2 mb-2">
            <Alert severity="success" className="p-3 text-green-700 text-sm font-medium">
              بدون خروج از منزل، آنلاین ویزیت شوید.
            </Alert>
            <div onClick={() => handleClickDcotorCardDoctor({ url: substituteDoctor.url, doctor: substituteDoctor })}>
              <SearchCard
                avatarSize="lg"
                baseInfo={{
                  displayName: substituteDoctor.title,
                  expertise: substituteDoctor.display_expertise,
                  experience: substituteDoctor.experience,
                  isVerify: true,
                  avatar: substituteDoctor.image,
                  rate: raviRate,
                  isOnline: true,
                }}
                type="doctor"
                className="shadow-none !py-0 lg:!py-0 cursor-pointer"
                actions={[
                  {
                    text: `گفت‌وگو با ${substituteDoctor.title}`,
                    outline: false,
                    description: '',
                  },
                ]}
              />
            </div>
          </div>
        )}
        <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={handleClickMoreDoctors}>
          مشاهده سایر سرویس دهندگان آنلاین {selectedCategory.title}
        </Button>
      </Modal>
    </div>
  );
};

export default ConsultBanner;
